import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { Star as StarIcon, StarBorder as StarBorderIcon, StarHalf as StarHalfIcon } from '@mui/icons-material';

// Everything is stored on a 0-10 scale. The admin's chosen scale decides how it is shown.
export const isStars = (scaleType) => scaleType !== 'SCORE_10';
export const toScale = (avg10, scaleType) => (isStars(scaleType) ? (Number(avg10) || 0) / 2 : Number(avg10) || 0);
export const scaleMax = (scaleType) => (isStars(scaleType) ? 5 : 10);

export function scoreColor(avg10) {
  const v = Number(avg10) || 0;
  if (v >= 8) return '#2e7d32';
  if (v >= 6) return '#f9a825';
  return '#d32f2f';
}

export function StarRow({ value, size = 18 }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map((n) => {
        if (v >= n - 0.25) return <StarIcon key={n} sx={{ fontSize: size, color: '#FFB300' }} />;
        if (v >= n - 0.75) return <StarHalfIcon key={n} sx={{ fontSize: size, color: '#FFB300' }} />;
        return <StarBorderIcon key={n} sx={{ fontSize: size, color: '#cfd8dc' }} />;
      })}
    </Box>
  );
}

// Shows a 0-10 average as stars (1-5) or as a score out of 10, following the admin's setting.
export function ScoreView({ avg10, scaleType, size = 18, showBoth = false }) {
  const shown = toScale(avg10, scaleType);
  if (isStars(scaleType)) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <StarRow value={shown} size={size} />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{shown.toFixed(1)}</Typography>
        {showBoth && <Typography variant="caption" color="text.secondary">({Number(avg10).toFixed(1)}/10)</Typography>}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontWeight: 800, fontSize: size + 2, color: scoreColor(avg10) }}>{shown.toFixed(1)}</Typography>
      <Typography variant="caption" color="text.secondary">/ 10</Typography>
      {showBoth && <Typography variant="caption" color="text.secondary">({(shown / 2).toFixed(1)} stars)</Typography>}
    </Box>
  );
}

export function CriterionBars({ criteria, scaleType, dense = false }) {
  const max = scaleMax(scaleType);
  return (
    <Box>
      {(criteria || []).map((c) => {
        const shown = toScale(c.avg10, scaleType);
        return (
          <Box key={c.name} sx={{ mb: dense ? 0.75 : 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '75%' }}>{c.name}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{shown.toFixed(1)}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={Math.min(100, (shown / max) * 100)}
              sx={{ height: dense ? 5 : 7, borderRadius: 4, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: scoreColor(c.avg10) } }} />
          </Box>
        );
      })}
    </Box>
  );
}

export function Distribution({ counts }) {
  const total = (counts || []).reduce((a, b) => a + b, 0);
  return (
    <Box>
      {[5, 4, 3, 2, 1].map((n) => {
        const c = (counts || [])[n - 1] || 0;
        return (
          <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{ width: 28 }}>{n} <StarIcon sx={{ fontSize: 11, color: '#FFB300', verticalAlign: '-1px' }} /></Typography>
            <LinearProgress variant="determinate" value={total ? (c / total) * 100 : 0}
              sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: '#FFB300' } }} />
            <Typography variant="caption" sx={{ width: 22, textAlign: 'right' }}>{c}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
